import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Separator } from '../components/ui/separator';
import { 
  Save, 
  CheckCircle2, 
  XCircle, 
  Loader2,
  RefreshCw,
  Eye,
  EyeOff,
  ExternalLink,
  Info
} from 'lucide-react';
import { toast } from 'sonner';
import PageHelp from '../components/PageHelp';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function Integrations() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showSecrets, setShowSecrets] = useState({});
  
  const [formData, setFormData] = useState({
    // Twilio
    twilio_account_sid: '',
    twilio_auth_token: '',
    twilio_phone_number: '',
    // Resend
    resend_api_key: '',
    resend_from_email: '',
    // AWS
    aws_access_key_id: '',
    aws_secret_access_key: '',
    aws_s3_bucket: '',
    aws_region: '',
    // OpenAI
    openai_api_key: ''
  });

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/admin/integrations`);
      setSettings(response.data);
      
      // Pre-fill non-secret fields
      setFormData(prev => ({
        ...prev,
        twilio_phone_number: response.data.twilio_phone_number || '',
        resend_from_email: response.data.resend_from_email || '',
        aws_s3_bucket: response.data.aws_s3_bucket || '',
        aws_region: response.data.aws_region || ''
      }));
    } catch (error) {
      console.error('Failed to fetch settings:', error);
      toast.error('Failed to load integration settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const handleSave = async (section) => {
    try {
      setSaving(true);
      
      let updateData = {};
      
      switch (section) {
        case 'twilio':
          updateData = {
            twilio_account_sid: formData.twilio_account_sid || null,
            twilio_auth_token: formData.twilio_auth_token || null,
            twilio_phone_number: formData.twilio_phone_number || null
          };
          break;
        case 'resend':
          updateData = {
            resend_api_key: formData.resend_api_key || null,
            resend_from_email: formData.resend_from_email || null
          };
          break;
        case 'aws':
          updateData = {
            aws_access_key_id: formData.aws_access_key_id || null,
            aws_secret_access_key: formData.aws_secret_access_key || null,
            aws_s3_bucket: formData.aws_s3_bucket || null,
            aws_region: formData.aws_region || null
          };
          break;
        case 'openai':
          updateData = {
            openai_api_key: formData.openai_api_key || null
          };
          break;
        default:
          break;
      }
      
      await axios.put(`${API_URL}/api/admin/integrations`, updateData);
      toast.success(`${section.charAt(0).toUpperCase() + section.slice(1)} settings saved`);
      fetchSettings();
    } catch (error) {
      console.error('Failed to save settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const testS3Connection = async () => {
    try {
      const response = await axios.post(`${API_URL}/api/admin/integrations/test-s3`);
      if (response.data.success) {
        toast.success('S3 connection successful!');
      } else {
        toast.error(response.data.error || 'S3 connection failed');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to test S3 connection');
    }
  };

  const toggleSecret = (field) => {
    setShowSecrets(prev => ({ ...prev, [field]: !prev[field] }));
  };

  const StatusBadge = ({ configured }) => (
    <Badge variant={configured ? "default" : "secondary"} className={configured ? "bg-emerald-100 text-emerald-700" : ""}>
      {configured ? (
        <><CheckCircle2 className="h-3 w-3 mr-1" /> Configured</>
      ) : (
        <><XCircle className="h-3 w-3 mr-1" /> Not Configured</>
      )}
    </Badge>
  );

  const SourceBadge = ({ source }) => {
    if (!source || source === 'not_configured') return null;
    
    return (
      <Badge 
        variant="outline" 
        className={source === 'environment' ? "bg-blue-50 text-blue-700 border-blue-200" : "bg-slate-50 text-slate-600"}
      >
        {source === 'environment' ? 'From Environment' : 'From Database'}
      </Badge>
    );
  };

  const SecretInput = ({ field, value, onChange, placeholder }) => (
    <div className="relative">
      <Input
        type={showSecrets[field] ? "text" : "password"}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className="pr-10"
      />
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="absolute right-0 top-0 h-full px-3"
        onClick={() => toggleSecret(field)}
      >
        {showSecrets[field] ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
      </Button>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Integrations</h1>
          <p className="text-muted-foreground">Configure external service connections</p>
        </div>
        <div className="flex gap-2">
          <PageHelp pageKey="integrations" />
          <Button variant="outline" onClick={fetchSettings}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      <Tabs defaultValue="twilio" className="w-full">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="twilio">Twilio</TabsTrigger>
          <TabsTrigger value="resend">Resend</TabsTrigger>
          <TabsTrigger value="aws">AWS S3</TabsTrigger>
          <TabsTrigger value="openai">OpenAI</TabsTrigger>
        </TabsList>

        {/* Twilio */}
        <TabsContent value="twilio" className="mt-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    Twilio SMS
                    <StatusBadge configured={settings?.twilio_configured} />
                    <SourceBadge source={settings?.twilio_source} />
                  </CardTitle>
                  <CardDescription>For sending SMS notifications to applicants</CardDescription>
                </div>
                <a href="https://console.twilio.com" target="_blank" rel="noopener noreferrer" 
                   className="text-sm text-blue-600 hover:underline flex items-center gap-1">
                  Twilio Console <ExternalLink className="h-3 w-3" />
                </a>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {settings?.twilio_source === 'environment' && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-700">
                  <Info className="h-4 w-4 inline mr-2" />
                  Twilio credentials are set via environment variables. Database values below are used as fallback.
                </div>
              )}
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label>Account SID</Label>
                  <SecretInput 
                    field="twilio_account_sid"
                    value={formData.twilio_account_sid}
                    onChange={(e) => setFormData({ ...formData, twilio_account_sid: e.target.value })}
                    placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Auth Token</Label>
                  <SecretInput 
                    field="twilio_auth_token"
                    value={formData.twilio_auth_token}
                    onChange={(e) => setFormData({ ...formData, twilio_auth_token: e.target.value })}
                    placeholder="Your auth token"
                  />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label>SMS Phone Number</Label>
                  <Input 
                    value={formData.twilio_phone_number}
                    onChange={(e) => setFormData({ ...formData, twilio_phone_number: e.target.value })}
                    placeholder="+1234567890"
                  />
                  <p className="text-xs text-muted-foreground">Your Twilio phone number in E.164 format</p>
                </div>
              </div>
              <Separator />
              <Button onClick={() => handleSave('twilio')} disabled={saving}>
                {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                Save Twilio Settings
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Resend */}
        <TabsContent value="resend" className="mt-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    Resend Email
                    <StatusBadge configured={settings?.resend_configured} />
                    <SourceBadge source={settings?.resend_source} />
                  </CardTitle>
                  <CardDescription>For sending email notifications</CardDescription>
                </div>
                <a href="https://resend.com" target="_blank" rel="noopener noreferrer" 
                   className="text-sm text-blue-600 hover:underline flex items-center gap-1">
                  Resend Dashboard <ExternalLink className="h-3 w-3" />
                </a>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {settings?.resend_source === 'environment' && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-700">
                  <Info className="h-4 w-4 inline mr-2" />
                  Resend credentials are set via environment variables. Database values below are used as fallback.
                </div>
              )}
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label>API Key</Label>
                  <SecretInput 
                    field="resend_api_key"
                    value={formData.resend_api_key}
                    onChange={(e) => setFormData({ ...formData, resend_api_key: e.target.value })}
                    placeholder="re_xxxxxxxxxx"
                  />
                </div>
                <div className="space-y-2">
                  <Label>From Email Address</Label>
                  <Input 
                    value={formData.resend_from_email}
                    onChange={(e) => setFormData({ ...formData, resend_from_email: e.target.value })}
                    placeholder="verify@yourdomain.com"
                  />
                </div>
              </div>
              <Separator />
              <Button onClick={() => handleSave('resend')} disabled={saving}>
                {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                Save Resend Settings
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* AWS S3 */}
        <TabsContent value="aws" className="mt-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    AWS S3 Storage
                    <StatusBadge configured={settings?.aws_configured} />
                    <SourceBadge source={settings?.aws_source} />
                  </CardTitle>
                  <CardDescription>For storing evidence files securely</CardDescription>
                </div>
                <a href="https://console.aws.amazon.com/s3" target="_blank" rel="noopener noreferrer" 
                   className="text-sm text-blue-600 hover:underline flex items-center gap-1">
                  AWS Console <ExternalLink className="h-3 w-3" />
                </a>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {settings?.aws_source === 'environment' && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-700">
                  <Info className="h-4 w-4 inline mr-2" />
                  AWS credentials are set via environment variables. Database values below are used as fallback.
                </div>
              )}
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label>Access Key ID</Label>
                  <SecretInput 
                    field="aws_access_key_id"
                    value={formData.aws_access_key_id}
                    onChange={(e) => setFormData({ ...formData, aws_access_key_id: e.target.value })}
                    placeholder="AKIAIOSFODNN7EXAMPLE"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Secret Access Key</Label>
                  <SecretInput 
                    field="aws_secret_access_key"
                    value={formData.aws_secret_access_key}
                    onChange={(e) => setFormData({ ...formData, aws_secret_access_key: e.target.value })}
                    placeholder="wJalrXUtnFEMI/K7MDENG/bPxRfiCYEXAMPLEKEY"
                  />
                </div>
                <div className="space-y-2">
                  <Label>S3 Bucket Name</Label>
                  <Input 
                    value={formData.aws_s3_bucket}
                    onChange={(e) => setFormData({ ...formData, aws_s3_bucket: e.target.value })}
                    placeholder="my-bucket-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Region</Label>
                  <Input 
                    value={formData.aws_region}
                    onChange={(e) => setFormData({ ...formData, aws_region: e.target.value })}
                    placeholder="us-east-1"
                  />
                </div>
              </div>
              <Separator />
              <div className="flex gap-2">
                <Button onClick={() => handleSave('aws')} disabled={saving}>
                  {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                  Save AWS Settings
                </Button>
                <Button variant="outline" onClick={testS3Connection}>
                  Test Connection
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* OpenAI */}
        <TabsContent value="openai" className="mt-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    OpenAI
                    <StatusBadge configured={settings?.openai_configured} />
                    <SourceBadge source={settings?.openai_source} />
                  </CardTitle>
                  <CardDescription>For AI-powered document analysis (text only - GDPR compliant)</CardDescription>
                </div>
                <a href="https://platform.openai.com/api-keys" target="_blank" rel="noopener noreferrer" 
                   className="text-sm text-blue-600 hover:underline flex items-center gap-1">
                  OpenAI Dashboard <ExternalLink className="h-3 w-3" />
                </a>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {settings?.openai_source === 'environment' && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-700">
                  <Info className="h-4 w-4 inline mr-2" />
                  OpenAI API key is set via environment variable. Database value below is used as fallback.
                </div>
              )}
              <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                <div className="flex items-start gap-2">
                  <Info className="h-4 w-4 text-green-600 mt-0.5" />
                  <div className="text-sm text-green-800">
                    <p className="font-medium">GDPR Compliant Processing</p>
                    <p>Images are processed locally using Tesseract OCR and DeepFace. Only extracted text data is sent to OpenAI for risk analysis. No images or biometric data leave your server.</p>
                  </div>
                </div>
              </div>
              <div className="space-y-2">
                <Label>API Key</Label>
                <SecretInput 
                  field="openai_api_key"
                  value={formData.openai_api_key}
                  onChange={(e) => setFormData({ ...formData, openai_api_key: e.target.value })}
                  placeholder="sk-xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                />
                <p className="text-xs text-muted-foreground">Set OPENAI_API_KEY environment variable for production deployments</p>
              </div>
              <Separator />
              <Button onClick={() => handleSave('openai')} disabled={saving}>
                {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                Save OpenAI Settings
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

export default Integrations;
